import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/hooks/ui/use-toast';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  AlertCircle,
  CheckCircle2,
  XCircle,
  MessageSquare,
  Target,
  Lightbulb,
  Clock,
  ChevronRight,
  RefreshCw,
  Eye,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface FeedbackIntelligenceDashboardProps {
  businessId: string;
}

interface FeedbackPattern {
  id: string;
  pattern_type: string;
  topic: string;
  direction: 'improving' | 'declining' | 'stable';
  confidence_score: number;
  occurrence_count: number;
  summary: string;
  sentiment_average: number | null;
  sentiment_previous: number | null;
  is_actionable: boolean;
  priority: 'low' | 'medium' | 'high' | 'critical';
  created_at: string;
}

interface Recommendation {
  id: string;
  recommendation_type: string;
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  status: 'proposed' | 'accepted' | 'in_progress' | 'completed' | 'dismissed';
  expected_impact: string;
  estimated_effort: 'minimal' | 'moderate' | 'significant';
  suggested_actions: string[];
  related_topic: string;
  created_at: string;
}

interface InvestigationQuestion {
  id: string;
  question_text: string;
  target_topic: string;
  priority: number;
  responses_received: number;
  max_responses: number | null;
  is_active: boolean;
  active_from: string;
  active_until: string | null;
  created_at: string;
}

const priorityColors: Record<string, string> = {
  low: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  medium: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
  critical: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
};

const statusColors: Record<string, string> = {
  proposed: 'bg-yellow-100 text-yellow-700',
  accepted: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-purple-100 text-purple-700',
  completed: 'bg-green-100 text-green-700',
  dismissed: 'bg-gray-100 text-gray-500',
};

const DirectionIcon: React.FC<{ direction: string }> = ({ direction }) => {
  switch (direction) {
    case 'improving':
      return <TrendingUp className="h-4 w-4 text-green-500" />;
    case 'declining':
      return <TrendingDown className="h-4 w-4 text-red-500" />;
    default:
      return <Minus className="h-4 w-4 text-gray-400" />;
  }
};

export const FeedbackIntelligenceDashboard: React.FC<FeedbackIntelligenceDashboardProps> = ({
  businessId,
}) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('patterns');

  // Fetch feedback patterns
  const { data: patterns, isLoading: patternsLoading } = useQuery({
    queryKey: ['feedback-patterns', businessId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('feedback_patterns')
        .select('*')
        .eq('business_id', businessId)
        .eq('is_active', true)
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as FeedbackPattern[];
    },
  });

  // Fetch recommendations
  const { data: recommendations, isLoading: recommendationsLoading } = useQuery({
    queryKey: ['ai-recommendations', businessId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ai_recommendations')
        .select('*')
        .eq('business_id', businessId)
        .neq('status', 'dismissed')
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Recommendation[];
    },
  });

  // Fetch investigation questions
  const { data: investigations, isLoading: investigationsLoading } = useQuery({
    queryKey: ['investigation-questions', businessId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('investigation_questions')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      return data as InvestigationQuestion[];
    },
  });

  // Update recommendation status mutation
  const updateRecommendationMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase
        .from('ai_recommendations')
        .update({
          status,
          ...(status === 'accepted' ? { accepted_at: new Date().toISOString() } : {}),
          ...(status === 'completed' ? { completed_at: new Date().toISOString() } : {}),
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-recommendations', businessId] });
      toast.success('Recommendation updated');
    },
    onError: (error) => {
      toast.error('Failed to update recommendation', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  // Toggle investigation question status
  const toggleInvestigationMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase
        .from('investigation_questions')
        .update({ is_active: isActive })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investigation-questions', businessId] });
      toast.success('Investigation question updated');
    },
  });

  const pendingRecommendations = recommendations?.filter(r => r.status === 'proposed') || [];
  const activeInvestigations = investigations?.filter(i => i.is_active) || [];
  const criticalPatterns = patterns?.filter(p => p.priority === 'critical' || p.priority === 'high') || [];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Patterns</p>
                <p className="text-2xl font-bold">{patterns?.length || 0}</p>
              </div>
              <Target className="h-8 w-8 text-blue-500 opacity-20" />
            </div>
            {criticalPatterns.length > 0 && (
              <p className="text-xs text-orange-500 mt-2 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {criticalPatterns.length} need attention
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Actions</p>
                <p className="text-2xl font-bold">{pendingRecommendations.length}</p>
              </div>
              <Lightbulb className="h-8 w-8 text-yellow-500 opacity-20" />
            </div>
            {pendingRecommendations.length > 0 && (
              <p className="text-xs text-muted-foreground mt-2">
                {pendingRecommendations.filter(r => r.priority === 'high' || r.priority === 'critical').length} high priority
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Investigations</p>
                <p className="text-2xl font-bold">{activeInvestigations.length}</p>
              </div>
              <MessageSquare className="h-8 w-8 text-purple-500 opacity-20" />
            </div>
            {activeInvestigations.length > 0 && (
              <p className="text-xs text-muted-foreground mt-2">
                {activeInvestigations.reduce((sum, i) => sum + i.responses_received, 0)} responses collected
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Completed Actions</p>
                <p className="text-2xl font-bold">
                  {recommendations?.filter(r => r.status === 'completed').length || 0}
                </p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="patterns" className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            Patterns
            {criticalPatterns.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                {criticalPatterns.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="recommendations" className="flex items-center gap-2">
            <Lightbulb className="h-4 w-4" />
            Recommendations
            {pendingRecommendations.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                {pendingRecommendations.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="investigations" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Investigations
          </TabsTrigger>
        </TabsList>

        {/* Patterns Tab */}
        <TabsContent value="patterns" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Detected Feedback Patterns</CardTitle>
              <CardDescription>
                AI-detected trends from customer feedback analysis
              </CardDescription>
            </CardHeader>
            <CardContent>
              {patternsLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <Skeleton key={i} className="h-20 w-full" />
                  ))}
                </div>
              ) : patterns?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Target className="h-12 w-12 mx-auto mb-3 opacity-20" />
                  <p>No active patterns detected yet.</p>
                  <p className="text-sm mt-1">Patterns will appear as feedback is analyzed.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {patterns?.map(pattern => (
                    <div
                      key={pattern.id}
                      className="p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <DirectionIcon direction={pattern.direction} />
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium capitalize">
                                {pattern.topic.replace(/_/g, ' ')}
                              </span>
                              <Badge className={priorityColors[pattern.priority]} variant="secondary">
                                {pattern.priority}
                              </Badge>
                              {pattern.is_actionable && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <AlertCircle className="h-3.5 w-3.5 text-orange-500" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Actionable insight</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              {pattern.summary}
                            </p>
                          </div>
                        </div>
                        <div className="text-right text-sm text-muted-foreground">
                          <div>{pattern.occurrence_count} mentions</div>
                          <div className="text-xs">
                            {Math.round(pattern.confidence_score * 100)}% confidence
                          </div>
                        </div>
                      </div>
                      {pattern.sentiment_average !== null && (
                        <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                          <span>
                            Sentiment: {pattern.sentiment_average.toFixed(2)}
                            {pattern.sentiment_previous !== null && (
                              <span className="ml-1">
                                (was {pattern.sentiment_previous.toFixed(2)})
                              </span>
                            )}
                          </span>
                          <Progress
                            value={(pattern.sentiment_average + 1) * 50}
                            className="h-1.5 w-24"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Recommendations Tab */}
        <TabsContent value="recommendations" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">AI Recommendations</CardTitle>
              <CardDescription>
                Proposed actions based on feedback analysis
              </CardDescription>
            </CardHeader>
            <CardContent>
              {recommendationsLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <Skeleton key={i} className="h-32 w-full" />
                  ))}
                </div>
              ) : recommendations?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Lightbulb className="h-12 w-12 mx-auto mb-3 opacity-20" />
                  <p>No recommendations yet.</p>
                  <p className="text-sm mt-1">
                    Recommendations are generated automatically when patterns are detected.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recommendations?.map(rec => (
                    <div
                      key={rec.id}
                      className="p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{rec.title}</span>
                            <Badge className={priorityColors[rec.priority]} variant="secondary">
                              {rec.priority}
                            </Badge>
                            <Badge className={statusColors[rec.status]} variant="secondary">
                              {rec.status.replace('_', ' ')}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {rec.description}
                          </p>
                        </div>
                      </div>

                      {rec.suggested_actions && rec.suggested_actions.length > 0 && (
                        <div className="mt-3 pl-4 border-l-2 border-muted">
                          <p className="text-xs font-medium text-muted-foreground mb-1">
                            Suggested Actions:
                          </p>
                          <ul className="text-sm space-y-1">
                            {rec.suggested_actions.slice(0, 3).map((action, i) => (
                              <li key={i} className="flex items-center gap-2 text-muted-foreground">
                                <ChevronRight className="h-3 w-3" />
                                {action}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="flex items-center justify-between mt-4 pt-3 border-t">
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>Impact: {rec.expected_impact}</span>
                          <span>Effort: {rec.estimated_effort}</span>
                          <span>Topic: {rec.related_topic?.replace(/_/g, ' ')}</span>
                        </div>

                        {rec.status === 'proposed' && (
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                updateRecommendationMutation.mutate({
                                  id: rec.id,
                                  status: 'dismissed',
                                })
                              }
                              disabled={updateRecommendationMutation.isPending}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Dismiss
                            </Button>
                            <Button
                              size="sm"
                              onClick={() =>
                                updateRecommendationMutation.mutate({
                                  id: rec.id,
                                  status: 'accepted',
                                })
                              }
                              disabled={updateRecommendationMutation.isPending}
                            >
                              <CheckCircle2 className="h-4 w-4 mr-1" />
                              Accept
                            </Button>
                          </div>
                        )}

                        {rec.status === 'accepted' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              updateRecommendationMutation.mutate({
                                id: rec.id,
                                status: 'in_progress',
                              })
                            }
                            disabled={updateRecommendationMutation.isPending}
                          >
                            <Clock className="h-4 w-4 mr-1" />
                            Start Working
                          </Button>
                        )}

                        {rec.status === 'in_progress' && (
                          <Button
                            size="sm"
                            onClick={() =>
                              updateRecommendationMutation.mutate({
                                id: rec.id,
                                status: 'completed',
                              })
                            }
                            disabled={updateRecommendationMutation.isPending}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" />
                            Mark Complete
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Investigations Tab */}
        <TabsContent value="investigations" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Investigation Questions</CardTitle>
              <CardDescription>
                Targeted questions to gather specific feedback
              </CardDescription>
            </CardHeader>
            <CardContent>
              {investigationsLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <Skeleton key={i} className="h-20 w-full" />
                  ))}
                </div>
              ) : investigations?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-20" />
                  <p>No investigation questions yet.</p>
                  <p className="text-sm mt-1">
                    Use AI chat to create investigation questions for specific topics.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {investigations?.map(inv => (
                    <div
                      key={inv.id}
                      className={`p-4 border rounded-lg transition-colors ${
                        inv.is_active ? 'hover:bg-muted/50' : 'opacity-60'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge
                              variant={inv.is_active ? 'default' : 'secondary'}
                              className="text-xs"
                            >
                              {inv.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                            <span className="text-xs text-muted-foreground capitalize">
                              {inv.target_topic.replace(/_/g, ' ')}
                            </span>
                          </div>
                          <p className="font-medium">{inv.question_text}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Eye className="h-3 w-3" />
                              {inv.responses_received} responses
                              {inv.max_responses && ` / ${inv.max_responses} max`}
                            </span>
                            <span>Priority: {inv.priority}</span>
                            {inv.active_until && (
                              <span>
                                Expires: {new Date(inv.active_until).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            toggleInvestigationMutation.mutate({
                              id: inv.id,
                              isActive: !inv.is_active,
                            })
                          }
                          disabled={toggleInvestigationMutation.isPending}
                        >
                          {inv.is_active ? (
                            <>
                              <XCircle className="h-4 w-4 mr-1" />
                              Deactivate
                            </>
                          ) : (
                            <>
                              <RefreshCw className="h-4 w-4 mr-1" />
                              Reactivate
                            </>
                          )}
                        </Button>
                      </div>
                      {inv.max_responses && (
                        <div className="mt-3">
                          <Progress
                            value={(inv.responses_received / inv.max_responses) * 100}
                            className="h-1.5"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FeedbackIntelligenceDashboard;
